import React, { useState, useEffect } from 'react';
import { ChevronRight, Menu, X, Target, Globe, Users, GraduationCap, Search, Award, Code, Shield } from 'lucide-react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ResearchTimeline from './components/Research-Timeline';
import { color } from 'framer-motion';

const About = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  useEffect(() => {
    // This effect manages the stylesheet for hovers and responsiveness
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      /* --- Hover Effects --- */
      [data-component="announcement"]:hover { background-color: rgba(16, 185, 129, 0.15); }
      [data-component="primary-btn"]:hover { background-color: #059669; }
      [data-component="secondary-btn"]:hover { color: #10b981; }
      [data-component="purpose-card"]:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
      }
      [data-component="offering-card"]:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border-color: #10b981;
      }

      /* --- Responsive Design --- */
      @media (max-width: 768px) {
        .hide-on-mobile { display: none !important; }
        .show-on-mobile { display: block !important; }
        
        [data-component="hero-title"] { font-size: 2.5rem !important; }
        [data-component="hero-subtitle"] { font-size: 1.1rem !important; }
        [data-component="button-container"] { flex-direction: column !important; }
        [data-component="primary-btn"] { width: 100% !important; }
        
        [data-component="purpose-section"],
        [data-component="impact-section"],
        [data-component="offerings-section"] {
          padding: 60px 20px !important;
        }

        [data-component="purpose-title"],
        [data-component="offerings-title"] {
          font-size: 38px !important;
        }

        [data-component="purpose-card"] {
          width: 82% !important;
          margin: 0 auto !important;
        }

        [data-component="impact-title"] { font-size: 2.25rem !important; }
        [data-component="stats-grid"] { grid-template-columns: 1fr !important; }
        [data-component="stat-number"] { font-size: 2.5rem !important; }
        
        [data-component="offerings-grid"] { grid-template-columns: 1fr !important; }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => { document.head.removeChild(styleSheet); };
  }, []);

  const cards = [
    { icon: <Target size={24} color="white" />, title: "Our Mission", description: "To democratize AI education by making high-quality learning resources accessible to everyone, regardless of their background or location." },
    { icon: <Globe size={24} color="white" />, title: "Our Vision", description: "A world where AI innovation is driven by diverse voices and inclusive perspectives, creating technology that benefits all of humanity." },
    { icon: <Users size={24} color="white" />, title: "Our Impact", description: "Building a global network of AI practitioners who are equipped with both technical skills and ethical understanding to shape the future responsibly." }
  ];
  const stats = [ { number: "50k+", label: "Members in our global community" }, { number: "25+", label: "Countries reached worldwide" }, { number: "50+", label: "Multilingual AI Courses available" }, { number: "100+", label: "Active research projects" } ];
  const offerings = [
    { icon: <GraduationCap size={20} color="#10b981" />, title: "AI Education Courses", description: "Comprehensive curriculum from beginner to advanced levels." },
    { icon: <Search size={20} color="#10b981" />, title: "Research Initiatives", description: "Collaborative projects addressing real-world challenges." },
    { icon: <Users size={20} color="#10b981" />, title: "Community Forums", description: "Global network for knowledge sharing and mentorship." },
    { icon: <Award size={20} color="#10b981" />, title: "Certification Programs", description: "Industry-recognized credentials in AI fields." },
    { icon: <Code size={20} color="#10b981" />, title: "Open Source Projects", description: "Contributing to the global AI development ecosystem." },
    { icon: <Shield size={20} color="#10b981" />, title: "Ethics & Policy", description: "Promoting responsible AI development and deployment." }
  ];
  
  // Base styles (simplified and consolidated)
  const styles = {
    pageWrapper: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    heroSection: { minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', paddingTop: '9em', paddingBottom: '80px', paddingLeft: '1.5rem', paddingRight: '1.5rem' },
    heroContent: { maxWidth: '1280px', margin: '0 auto', textAlign: 'center' },
    announcement: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#059669', padding: '0.5rem 1rem', borderRadius: '24px', fontSize: '14px', fontWeight: '500', marginBottom: '2rem', cursor: 'pointer', transition: 'all 0.3s ease' },
    // --- THIS IS THE ONLY LINE THAT WAS CHANGED ---
    heroTitle: { fontSize: '3rem', fontWeight: '800', lineHeight: '1.1', color: '#1f2937', marginBottom: '1.5rem', letterSpacing: '-0.02em' },
    heroSubtitle: { fontSize: '1.2rem', color: '#6b7280', lineHeight: '1.6', maxWidth: '600px', margin: '0 auto 2.5rem auto' },
    buttonContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '3em' },
    primaryBtn: { backgroundColor: '#10b981', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '12px', fontSize: '16px', fontWeight: '600', border: 'none', cursor: 'default', transition: 'all 0.3s ease', minWidth: '180px' },
    secondaryBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', textDecoration: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer', padding: '0.75rem 1rem', transition: 'color 0.3s ease' },
    section: { padding: '80px 20px' },
    sectionContainer: { maxWidth: '1200px', margin: '0 auto' },
    sectionHeader: { textAlign: 'center', marginBottom: '60px' },
    sectionTitle: { fontSize: '48px', fontWeight: '700', color: '#1f2937', marginBottom: '24px', lineHeight: '1.1' },
    sectionSubtitle: { fontSize: '18px', color: '#6b7280', lineHeight: '1.6', maxWidth: '600px', margin: '0 auto' },
    purposeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '32px', marginTop: '48px' },
    purposeCard: { backgroundColor: 'white', padding: '40px 30px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)', textAlign: 'left', transition: 'transform 0.3s ease, box-shadow 0.3s ease', border: '1px solid #e2e8f0' },
    purposeIcon: { width: '48px', height: '48px', backgroundColor: '#14b8a6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' },
    purposeCardTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' },
    purposeCardDesc: { fontSize: '16px', color: '#64748b', lineHeight: '1.6' },
    impactSection: { backgroundColor: '#f9fafb' },
    impactTitle: { fontSize: '2.5rem', fontWeight: '700', color: '#111827', marginBottom: '16px', lineHeight: '1.1' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '32px', marginTop: '48px' },
    statCard: { textAlign: 'center' },
    statNumber: { fontSize: '3rem', fontWeight: '700', color: '#10b981', marginBottom: '8px', lineHeight: '1' },
    statLabel: { color: '#374151', fontSize: '16px', lineHeight: '1.6', fontWeight: '500' },
    offeringsSection: { backgroundColor: '#ffffff', margin: '4em 0 7em' },
    offeringsSubtitle: { fontSize: '16px', color: '#10b981', fontWeight: '600', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    offeringsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '32px', marginTop: '48px' },
    offeringCard: { display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '24px', backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', transition: 'all 0.3s ease', cursor: 'pointer' },
    offeringIcon: { width: '40px', height: '40px', backgroundColor: '#f0fdf4', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    offeringCardTitle: { fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' },
    offeringCardDesc: { fontSize: '15px', color: '#6b7280', lineHeight: '1.6' },
  };

  return (
    <div style={styles.pageWrapper}>
      <Navbar />
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <div style={styles.announcement} data-component="announcement">
            Launching the Global AI Education Initiative. Read more <ChevronRight size={14} />
          </div>
          <h1 style={styles.heroTitle} data-component="title">Democratizing<br />AI Education<br />for Everyone</h1>
          <p style={styles.heroSubtitle} data-component="subtitle">
            Empowering the next generation of AI innovators through accessible education, cutting-edge research, and a global community.
          </p>
          <div style={styles.buttonContainer} data-component="button-container">
            <button style={styles.primaryBtn} data-component="primary-btn">Join Our Community</button>
          </div>
        </div>
      </div>

      <div style={{...styles.section, ...styles.purposeSection}} data-component="purpose-section">
        <div style={styles.sectionContainer}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle} data-component="purpose-title">Our Purpose & Promise</h2>
            <p style={styles.sectionSubtitle}>
              Driven by a shared commitment to democratize AI education and foster inclusive innovation that benefits everyone.
            </p>
          </div>
          <div style={styles.purposeGrid}>
            {cards.map((card, index) => (
              <div key={index} style={styles.purposeCard} data-component="purpose-card">
                <div style={styles.purposeIcon}>{card.icon}</div>
                <h3 style={styles.purposeCardTitle}>{card.title}</h3>
                <p style={styles.purposeCardDesc}>{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section style={{...styles.section, ...styles.impactSection}} data-component="impact-section">
        <div style={styles.sectionContainer}>
          <div style={styles.sectionHeader}>
            <h2 style={{...styles.sectionTitle, ...styles.impactTitle}} data-component="impact-title">Our Global Impact</h2>
            <p style={{...styles.sectionSubtitle, ...styles.impactSubtitle}}>
              Connecting AI enthusiasts and professionals worldwide to build a more inclusive future.
            </p>
          </div>
          <div style={styles.statsGrid} data-component="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} style={styles.statCard}>
                <div style={styles.statNumber} data-component="stat-number">{stat.number}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/*<ResearchTimeline />*/}

      <section style={styles.offeringsSection} data-component="offerings-section">
        <div style={styles.sectionContainer}>
          <div style={styles.sectionHeader}>
            <div style={styles.offeringsSubtitle}>Core Offerings</div>
            <h2 style={{...styles.sectionTitle, ...styles.offeringsTitle}} data-component="offerings-title">Empowering Global AI Literacy</h2>
            <p style={styles.sectionSubtitle}>
              Discover our comprehensive suite of programs designed to make AI education accessible to learners worldwide.
            </p>
          </div>
          <div style={styles.offeringsGrid} data-component="offerings-grid">
            {offerings.map((offering, index) => (
              <div key={index} style={styles.offeringCard} data-component="offering-card">
                <div style={styles.offeringIcon}>{offering.icon}</div>
                <div>
                  <h3 style={styles.offeringCardTitle}>{offering.title}</h3>
                  <p style={styles.offeringCardDesc}>{offering.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default About;